// Not ported (dead in the Angular app): getManagementPeriod, getPack
import { api, apiRequest, type QueryParams } from '@/core/http/apiClient';
import { download } from '@/core/http/files';
import { exportFile, readBlobError } from '@/core/http/helpers';
import type {
  BudgetGridResponse,
  CaptureDimensionsResult,
  DeleteBudgetsPayload,
  DimensionRow,
  GenerateManagementPeriodsPayload,
  GenerateManagementPeriodsResult,
  ManagementExportQuery,
  ManagementPackRow,
  ManagementPeriodRow,
  ManagementReportingOptions,
  MultiYearReportResponse,
  PeriodicTrialBalanceResponse,
  SaveBudgetsPayload,
  SaveDimensionPayload,
  SaveDimensionValuePayload,
  SaveManagementPackPayload,
  SaveManagementPeriodPayload,
  SnapshotResult,
  VarianceReportResponse,
} from '@/core/models/management-reporting.models';

const base = 'ManagementReporting/';

function dimensionParams(dimensionCode?: string | null, dimensionValue?: string | null): QueryParams {
  const params: QueryParams = {};
  if (dimensionCode) {
    params.dimensionCode = dimensionCode;
  }
  if (dimensionValue) {
    params.dimensionValue = dimensionValue;
  }
  return params;
}

const exportReport = (path: string, query: ManagementExportQuery) =>
  exportFile(path, query, !!query.isPrint, { unwrapBlobErrors: true });

async function downloadBlob(path: string): Promise<boolean> {
  try {
    const response = await apiRequest<Blob>('GET', path, { responseType: 'blob' });
    download(response);
    return true;
  } catch (error) {
    return readBlobError(error);
  }
}

export const managementReportingService = {
  getOptions: () => api.get<ManagementReportingOptions>(`${base}getOptions`),

  // ---- Management periods ----

  getManagementPeriods: (clientId: string) => api.get<ManagementPeriodRow[]>(`${base}getManagementPeriods/${clientId}`),

  saveManagementPeriod: (payload: SaveManagementPeriodPayload) =>
    api.post<ManagementPeriodRow>(`${base}saveManagementPeriod`, payload),

  generateManagementPeriods: (payload: GenerateManagementPeriodsPayload) =>
    api.post<GenerateManagementPeriodsResult>(`${base}generateManagementPeriods`, payload),

  setManagementPeriodStatus: (id: string, status: number) =>
    api.post<ManagementPeriodRow>(`${base}setManagementPeriodStatus/${id}`, { status }),

  deleteManagementPeriods: (ids: string[]) => api.post<boolean>(`${base}deleteManagementPeriods`, ids),

  snapshotManagementPeriod: (id: string, trialBalanceId: string) =>
    api.post<SnapshotResult>(`${base}snapshotManagementPeriod/${id}`, { trialBalanceId }),

  // ---- Reporting ----

  getPeriodicTrialBalance: (id: string, dimensionCode?: string | null, dimensionValue?: string | null) =>
    api.get<PeriodicTrialBalanceResponse>(`${base}getPeriodicTrialBalance/${id}`, {
      params: dimensionParams(dimensionCode, dimensionValue),
    }),

  exportPeriodicTrialBalance: (id: string, query: ManagementExportQuery) =>
    exportReport(`${base}exportPeriodicTrialBalance/${id}`, query),

  getVarianceReport: (
    id: string,
    priorManagementPeriodId?: string | null,
    dimensionCode?: string | null,
    dimensionValue?: string | null,
  ) => {
    const params = dimensionParams(dimensionCode, dimensionValue);
    if (priorManagementPeriodId) {
      params.priorManagementPeriodId = priorManagementPeriodId;
    }
    return api.get<VarianceReportResponse>(`${base}getVarianceReport/${id}`, { params });
  },

  exportVarianceReport: (id: string, query: ManagementExportQuery) =>
    exportReport(`${base}exportVarianceReport/${id}`, query),

  getMultiYearReport: (id: string, years: number, dimensionCode?: string | null, dimensionValue?: string | null) => {
    const params = { ...dimensionParams(dimensionCode, dimensionValue), years: String(years) };
    return api.get<MultiYearReportResponse>(`${base}getMultiYearReport/${id}`, { params });
  },

  exportMultiYearReport: (id: string, query: ManagementExportQuery) =>
    exportReport(`${base}exportMultiYearReport/${id}`, query),

  // ---- Budgets ----

  getBudgets: (id: string, dimensionCode?: string | null, dimensionValue?: string | null) =>
    api.get<BudgetGridResponse>(`${base}getBudgets/${id}`, {
      params: dimensionParams(dimensionCode, dimensionValue),
    }),

  saveBudgets: (payload: SaveBudgetsPayload) => api.post<BudgetGridResponse>(`${base}saveBudgets`, payload),

  deleteBudgets: (payload: DeleteBudgetsPayload) => api.post<boolean>(`${base}deleteBudgets`, payload),

  importBudgets: (id: string, file: File, dimensionCode?: string | null, dimensionValue?: string | null) => {
    const form = new FormData();
    form.append('file', file, file.name);
    return api.post<BudgetGridResponse>(`${base}importBudgets/${id}`, form, {
      params: dimensionParams(dimensionCode, dimensionValue),
    });
  },

  exportBudgets: (id: string, query: ManagementExportQuery) => exportReport(`${base}exportBudgets/${id}`, query),

  downloadBudgetTemplate: () => downloadBlob(`${base}getBudgetTemplate`),

  // ---- Dimensions ----

  getDimensions: (clientId?: string | null) =>
    api.get<DimensionRow[]>(`${base}getDimensions`, { params: clientId ? { clientId } : {} }),

  saveDimension: (payload: SaveDimensionPayload) => api.post<DimensionRow[]>(`${base}saveDimension`, payload),

  saveDimensionValue: (payload: SaveDimensionValuePayload) =>
    api.post<DimensionRow[]>(`${base}saveDimensionValue`, payload),

  deleteDimensions: (ids: string[]) => api.post<boolean>(`${base}deleteDimensions`, ids),

  deleteDimensionValues: (ids: string[]) => api.post<boolean>(`${base}deleteDimensionValues`, ids),

  captureDimensions: (clientId: string, accountingPeriodId?: string | null) =>
    api.post<CaptureDimensionsResult>(`${base}captureDimensions`, {
      clientId,
      accountingPeriodId: accountingPeriodId ?? null,
    }),

  // ---- Packs ----

  getPacks: (clientId: string) => api.get<ManagementPackRow[]>(`${base}getPacks/${clientId}`),

  savePack: (payload: SaveManagementPackPayload) => api.post<ManagementPackRow>(`${base}savePack`, payload),

  deletePacks: (ids: string[]) => api.post<boolean>(`${base}deletePacks`, ids),

  downloadPack: (id: string) => downloadBlob(`${base}downloadPack/${id}`),
};
